import { FC, Fragment, useCallback, useState } from "react";
import {
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import AddIcon from "@mui/icons-material/Add";

import { DashboardV2FoldersDeleteDialog } from "./DashboardV2FoldersDeleteDialog";
import { DashboardV2FoldersRenameDialog } from "./DashboardV2FoldersRenameDialog";
import { DashboardCreateFolderOrItemDialog } from "../create-folder-or-item-dialog/DashboardCreateFolderOrItemDialog";
import { navigationHierarchyItemNoDndKey } from "../../../sortable-tree/utils/constants";

interface DashboardV2FolderListItemActionsMenuProps {
  id: string;
  name: string;
  open: boolean;
  closeMenu(): void;
  anchor: HTMLElement | null;
}

export const DashboardV2FolderListItemActionsMenu: FC<
  DashboardV2FolderListItemActionsMenuProps
> = ({ name, id, open, anchor, closeMenu }) => {
  const [openDialogType, setOpenDialogType] = useState<
    "add-item" | "rename" | "delete"
  >();

  const renameClickHandler = useCallback(() => {
    setOpenDialogType("rename");
  }, []);

  const deleteClickHandler = useCallback(() => {
    setOpenDialogType("delete");
  }, []);

  const addClickHandler = useCallback(() => {
    setOpenDialogType("add-item");
  }, []);

  const closeDialog = useCallback(() => {
    setOpenDialogType(undefined);
    closeMenu();
  }, [closeMenu]);

  return (
    <Fragment>
      <Menu
        open={open}
        onClose={closeMenu}
        anchorEl={anchor}
        disablePortal={false}
        {...navigationHierarchyItemNoDndKey}
      >
        <MenuItem onClick={renameClickHandler} sx={menuItemSx}>
          <ListItemIcon>
            <EditOutlinedIcon fontSize="small" sx={iconSx} />
          </ListItemIcon>
          <ListItemText>
            <Typography variant="body2">Rename</Typography>
          </ListItemText>
        </MenuItem>

        <MenuItem onClick={addClickHandler} sx={menuItemSx}>
          <ListItemIcon>
            <AddIcon fontSize="small" sx={iconSx} />
          </ListItemIcon>
          <ListItemText>
            <Typography variant="body2">Add dashboard</Typography>
          </ListItemText>
        </MenuItem>
        <MenuItem onClick={deleteClickHandler} sx={menuItemSx}>
          <ListItemIcon>
            <DeleteOutlineIcon fontSize="small" sx={iconSx} />
          </ListItemIcon>
          <ListItemText>
            <Typography variant="body2">Delete</Typography>
          </ListItemText>
        </MenuItem>
      </Menu>

      {openDialogType === "delete" && (
        <DashboardV2FoldersDeleteDialog
          open
          id={id}
          name={name}
          closeMenu={closeDialog}
        />
      )}

      {openDialogType === "rename" && (
        <DashboardV2FoldersRenameDialog
          open
          id={id}
          name={name}
          closeMenu={closeDialog}
        />
      )}

      {openDialogType === "add-item" && (
        <DashboardCreateFolderOrItemDialog
          onClose={closeDialog}
          type="item"
          folderId={id}
        />
      )}
    </Fragment>
  );
};

const menuItemSx = {
  pl: 1,
  pr: 1.5,
};

const iconSx = {
  minWidth: 28,
};
